import { useNavigate, useSearchParams } from 'react-router-dom'
import { useMemo, useCallback } from 'react'
import { findShortcut, ShortcutSubcategory } from '../config/shortcuts'

/**
 * Hook for Dashboard: navigates to /generate with shortcut params
 */
export function useShortcutNavigation() {
  const navigate = useNavigate()

  const navigateWithShortcut = useCallback((categoryId: string, subcategoryId: string) => {
    const params = new URLSearchParams()
    params.set('category', categoryId)
    params.set('shortcut', subcategoryId)
    navigate(`/generate?${params.toString()}`)
  }, [navigate])

  return { navigateWithShortcut }
}

/**
 * Hook that returns both shortcut data and a function to clear params
 */
export function useShortcutParamsWithClear(): {
  shortcutData: ShortcutSubcategory | null
  clearShortcutParams: () => void
  hasShortcutParams: boolean
} {
  const [searchParams, setSearchParams] = useSearchParams()

  const categoryId = searchParams.get('category')
  const subcategoryId = searchParams.get('shortcut')
  const hasShortcutParams = !!(categoryId && subcategoryId)

  const shortcutData = useMemo(() => {
    if (!categoryId || !subcategoryId) {
      return null
    }
    return findShortcut(categoryId, subcategoryId)
  }, [categoryId, subcategoryId])

  const clearShortcutParams = useCallback(() => {
    const newParams = new URLSearchParams(searchParams)
    newParams.delete('category')
    newParams.delete('shortcut')
    setSearchParams(newParams, { replace: true })
  }, [searchParams, setSearchParams])

  return { shortcutData, clearShortcutParams, hasShortcutParams }
}
